import { IBadgeList } from '@faire/web-api--source/indigofair/badges/IBadgeList';
import { FreeShippingReason } from '@faire/web-api--source/indigofair/data/FreeShippingReason';
import { IApplicableMembershipBenefits } from '@faire/web-api--source/indigofair/data/IApplicableMembershipBenefits';
import { IBrandSearchQueryUXMessaging } from '@faire/web-api--source/indigofair/data/IBrandSearchQueryUXMessaging';
import { IFaireIcon } from '@faire/web-api--source/indigofair/data/IFaireIcon';
import { IFaireMoney } from '@faire/web-api--source/indigofair/data/IFaireMoney';
import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { IInsiderLabel } from '@faire/web-api--source/indigofair/data/IInsiderLabel';
import { IItemPromoPrice } from '@faire/web-api--source/indigofair/data/IItemPromoPrice';
import { IProduct } from '@faire/web-api--source/indigofair/data/IProduct';
import { IQuickAdd } from '@faire/web-api--source/indigofair/data/IQuickAdd';
import { IRetrievalAndRankingDebugInfoPerProduct } from '@faire/web-api--source/indigofair/data/IRetrievalAndRankingDebugInfoPerProduct';
import { ITaxonomyType } from '@faire/web-api--source/indigofair/data/ITaxonomyType';
import { ShowLocationBadging } from '@faire/web-api--source/indigofair/data/ShowLocationBadging';
import { ISO3166Alpha3 } from '@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3';

export namespace ILeanProductTile {
  export namespace ILeanProduct {
    /**
     * Return an instance of ILeanProduct with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<ILeanProduct>): ILeanProduct =>
      ({
        board_tokens: [],
        promoted_by: [],
        ...(partial ?? {}),
      }) as ILeanProduct;
  }

  export interface ILeanProduct {
    /**
     * @protobufOption (.faire.common.tokenType) = PRODUCT
     */
    token: string | undefined;
    name: string | undefined;
    /**
     * @protobufOption (.faire.common.tokenType) = BRAND
     */
    brand_token: string | undefined;
    is_new: boolean | undefined;
    is_high_sell_through: boolean | undefined;
    board_tokens: Array<string>;
    maker_best_seller: boolean | undefined;
    per_style_min_order_quantity: number | undefined;
    expected_ship_date: number | undefined;
    expected_ship_window_days: number | undefined;
    state: keyof typeof IProduct.State | undefined;
    /**
     * @deprecated
     */
    description: string | undefined;
    /**
     * @deprecated
     */
    taxonomy_type: ITaxonomyType | undefined;
    preorderable: boolean | undefined;
    is_promoted: boolean | undefined;
    promoted_by: Array<keyof typeof IProduct.PromotedType>;
    /**
     * @protobufOption (faire.common.diffIgnore) = ALWAYS
     */
    impression_id: string | undefined;
    /**
     * Formatted and localized avg brand review rating, i.e "4.8" in english or "4,8" in french.
     */
    avg_brand_review_rating: string | undefined;
    brand_review_count: number | undefined;
    /**
     * true when a product is set to state IProduct.State.SALES_PAUSED and there is only a single option
     * (aka product_option_token is defined) and the user has subscribed to notifications
     */
    is_subscribed_to_back_in_stock_notification: boolean | undefined;
    order_count_message: string | undefined;
  }

  export namespace IBrand {
    /**
     * Return an instance of IBrand with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IBrand>): IBrand =>
      ({
        ...(partial ?? {}),
      }) as IBrand;
  }

  export interface IBrand {
    /**
     * @protobufOption (.faire.common.tokenType) = BRAND
     */
    token: string | undefined;
    name: string | undefined;
    free_shipping_reason: keyof typeof FreeShippingReason | undefined;
    insider_benefits: IApplicableMembershipBenefits | undefined;
    insider_label: IInsiderLabel | undefined;
    forbids_online_only_retailers: boolean | undefined;
    minimum_order_amount: IFaireMoney | undefined;
    /**
     * Currently only used in product search, catnav in brandsByTokens in search service
     */
    amount_in_cart: IFaireMoney | undefined;
    ships_from_country: string | undefined;
    based_in: string | undefined;
    is_highlighted_brand_for_promotional_event: boolean | undefined;
    /**
     * True if the brand qualifies as a Top Shop for the current cycle
     */
    is_top_shop: boolean | undefined;
    active_products_count: number | undefined;
    based_in_country_message: IBrandSearchQueryUXMessaging | undefined;
    profile_image: IImage | undefined;
    /**
     * Feature was rolled back, and there is no replacement field
     * @deprecated
     */
    region_flag_image: IImage | undefined;
    squared_image: IImage | undefined;
    /**
     * Formatted and localized avg brand review rating, i.e "4.8" in english or "4,8" in french.
     */
    avg_brand_review_rating: string | undefined;
    /**
     * Icon to be displayed beside the brand name on product tiles
     */
    icon: IFaireIcon | undefined;
    /**
     * Formatted and localized description, populated for treatment case of ENABLE_SHIPS_FROM_ON_PRODUCT_TILES_241029
     */
    ships_from_message: string | undefined;
    /**
     * ISO country code for region flag lookup in treatment case of ENABLE_SHIPS_FROM_ON_PRODUCT_TILES_241029
     */
    flag_country_code: keyof typeof ISO3166Alpha3 | undefined;
  }

  export namespace IOptionsDetails {
    export namespace ISwatchDetails {
      /**
       * Return an instance of ISwatchDetails with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (
        partial?: Partial<ISwatchDetails>
      ): ISwatchDetails =>
        ({
          swatch_preview_images: [],
          swatches_and_options: [],
          ...(partial ?? {}),
        }) as ISwatchDetails;
    }

    /**
     * Holds the swatch information for a given product
     */
    export interface ISwatchDetails {
      /**
       * The swatch images to display. Possibly a subset of total swatches available.
       * DEPRECATION WARING: this will be replaced with swatchesAndOptions pending results of experiment
       * INTERACTABLE_TILE_SWATCHES_XP_250528
       */
      swatch_preview_images: Array<IImage>;
      swatches_and_options: Array<ILeanProductTile.IOptionsDetails.ISwatchAndOption>;
      /**
       * The formatted and localized number of remaining options excluding the ones shown in the swatchPreviewImages.
       * Basically the the total option count minus swatchPreviewImages.size.
       * e.g. "+12"
       */
      formatted_remaining_option_count: string | undefined;
      total_swatch_count: number | undefined;
    }

    export namespace ISwatchAndOption {
      /**
       * Return an instance of ISwatchAndOption with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (
        partial?: Partial<ISwatchAndOption>
      ): ISwatchAndOption =>
        ({
          ...(partial ?? {}),
        }) as ISwatchAndOption;
    }

    export interface ISwatchAndOption {
      /**
       * A small image representing a color/pattern/style variant of a product.
       * i.e could represent the "red" variant of a product
       */
      swatch_image: IImage | undefined;
      /**
       * A product option's image which features the color/pattern/style represented.
       * i.e could be the Small, Red option of a t-shirt which comes in colors red, blue and sizes small, large.
       */
      option_image: IImage | undefined;
      /**
       * The product option's token which corresponds to the optionImage
       * @protobufOption (.faire.common.tokenType) = PRODUCT_OPTION
       */
      option_token: string | undefined;
    }

    /**
     * Return an instance of IOptionsDetails with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IOptionsDetails>
    ): IOptionsDetails =>
      ({
        ...(partial ?? {}),
      }) as IOptionsDetails;
  }

  /**
   * Extra data about the options of the product
   */
  export interface IOptionsDetails {
    /**
     * Can be the number of options a product has, or the number of sizes. It'll be translated
     * in the backend so the FE can just display it if it's not null.
     */
    text: string | undefined;
    swatch_details: ILeanProductTile.IOptionsDetails.ISwatchDetails | undefined;
  }

  /**
   * Return an instance of ILeanProductTile with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<ILeanProductTile>
  ): ILeanProductTile =>
    ({
      images: [],
      settings_to_assign_on_view: [],
      ...(partial ?? {}),
    }) as ILeanProductTile;
}

export interface ILeanProductTile {
  product: ILeanProductTile.ILeanProduct | undefined;
  badge_list: IBadgeList | undefined;
  best_image: IImage | undefined;
  quick_add: IQuickAdd | undefined;
  min_option_wholesale_price: IFaireMoney | undefined;
  /**
   * Populated if a product has a brand promotion attached. If present, [minOptionWholesalePrice] is set to the
   * promotional price. If not present, the product is not on promotion.
   */
  min_option_wholesale_promo_price: IItemPromoPrice | undefined;
  min_option_retail_price: IFaireMoney | undefined;
  expected_shipping_date: string | undefined;
  /**
   * We don't actually need multiple images for search surfaces on FE. Because of the size of the
   *  image object, going forward, we'll just have bestImage here for backwards compatibility
   */
  images: Array<IImage>;
  has_active_brand_promo: boolean | undefined;
  is_highlighted_product_for_promotional_event: boolean | undefined;
  /**
   * Copy to show for case size, e.g. "Case of {caseSize}", "Prepacks Available", Min Qty {minimumOrderQuantity}
   */
  case_size_text: string | undefined;
  /**
   * Whether this product tile should be shown as a locked tile (no clickthrough to product details)
   */
  is_locked_view: boolean | undefined;
  /**
   * The SKU number assigned to the cheapest product option, by retail price, by the brand.
   * Temporarily ignoring diff as the order of options are different in services
   * @protobufOption (faire.common.diffIgnore) = ALWAYS
   */
  min_option_brand_code: string | undefined;
  /**
   * Retrieval debug info to be displayed by the FE for internal use only.
   */
  retrieval_and_ranking_debug_info:
    | IRetrievalAndRankingDebugInfoPerProduct
    | undefined;
  /**
   * aggregated promotion descriptions
   */
  brand_promo_text: string | undefined;
  based_in_country: string | undefined;
  show_location_badging: keyof typeof ShowLocationBadging | undefined;
  /**
   * Token of the ProductOption to pre-select in the PDP when the tile is clicked.
   * @protobufOption (.faire.common.tokenType) = PRODUCT_OPTION
   */
  selected_product_option_token: string | undefined;
  options_details: ILeanProductTile.IOptionsDetails | undefined;
  has_free_shipping_reason: boolean | undefined;
  has_insider_threshold_free_shipping: boolean | undefined;
  /**
   * Copy to show for availability information, e.g. "Preorder", "Only {lowInventory} left", "Out of stock"
   */
  availability_info: string | undefined;
  /**
   * Entry point to the similar products pop-up modal
   * @deprecated
   */
  find_similar_products: boolean | undefined;
  /**
   * @protobufOption (faire.common.diffAs) = UNORDERED
   */
  settings_to_assign_on_view: Array<string>;
}
